import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { CodigosAduana } from "src/app/models/Dte/Exportacion/CodigosAduana";
import { FormatoDte } from "src/app/models/Dte/FormatoDte";
import { IDte } from "src/app/models/Dte/IDte";
import { PlantillaDte } from "src/app/models/Dte/PlantillaDte";
import { DteEmitido } from "src/app/models/Dte/DteEmitido";
import { NotaCredito } from "src/app/models/NotaCredito";
import { NotaDebito } from "src/app/models/NotaDebito";
import { Parametros } from "src/app/models/Parametros";
import { Proveedor } from "src/app/models/Proveedor/Proveedor";
import { Receptor } from "src/app/models/Receptor/Receptor";
import { Response } from "src/app/shared/models/Response";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class FacturacionIndividualApiService {
	private readonly dteUrl = environment.backend_server + "/dte";
	private readonly clientesUrl = environment.backend_server + "/clientes";
	private readonly proveedoresUrl = environment.backend_server + "/proveedor";
	private readonly facturacionUrl = environment.backend_server + "/facturacion";
	private readonly boletaUrl = environment.backend_server + "/boleta";
	private readonly notaDebitoUrl = environment.backend_server + "/NotaDebito";
	private readonly guiaUrl = environment.backend_server + "/guiaDespacho";
	private readonly liquidacionUrl = environment.backend_server + "/liquidacionFactura";
	private readonly impresionUrl = environment.backend_server + "/Impresion";
	private readonly exportacionUrl = environment.backend_server + "/exportacion";

	constructor(private http: HttpClient) {}

	buscarCliente(rut: string, emisorId: string): Observable<Response<Receptor[]>> {
		const params = new HttpParams().set("pattern", rut || "").set("emisorId", emisorId || "");
		return this.http.get<Response<Receptor[]>>(this.clientesUrl + "/buscar", { params });
	}

	validarCliente(rut: string, emisorId: string): Observable<Response<boolean>> {
		const params = new HttpParams().set("rut", rut || "").set("emisorId", emisorId || "");
		return this.http.get<Response<boolean>>(this.clientesUrl + "/validar_por_emisor", { params });
	}

	buscarProveedor(rut: string, emisorId: string): Observable<Response<Proveedor[]>> {
		const params = new HttpParams().set("pattern", rut || "").set("emisorId", emisorId || "");
		return this.http.get<Response<Proveedor[]>>(this.proveedoresUrl + "/buscar", { params });
	}

	emitirFactura(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.http.post<Response<string>>(this.facturacionUrl + "/emitir", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	emitirGuia(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.http.post<Response<string>>(this.guiaUrl + "/emitir", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	emitirLiquidacion(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.http.post<Response<string>>(this.liquidacionUrl + "/emitir", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	emitirBoleta(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		return this.http.post<Response<string>>(this.boletaUrl + "/emitir", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	obtenerParametrosBoleta(): Observable<Response<Parametros>> {
		return this.http.get<Response<Parametros>>(this.boletaUrl + "/parametros");
	}

	previsualizar(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<string>> {
		let path = "/preview";
		if ([110, 111, 112].includes(dte.codigoTipoDte)) path += "/exporta";
		if (dte.codigoTipoDte === 43) path += "/liquidacion";
		return this.http.post<Response<string>>(this.impresionUrl + path, this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	emitirFacturaCompra(usuarioId: string, sucursalId: string, dte: IDte): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.facturacionUrl + "/emitir/facturacompra", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	emitirNotaDebito(usuarioId: string, sucursalId: string, nota: NotaDebito, dte: DteEmitido): Observable<Response<any>> {
		const formData = this.crearFormularioDte(usuarioId, sucursalId, dte);
		formData.append("motivo", nota.motivoString);
		switch (nota.tipoNota) {
			case 0:
				return this.http.post<Response<any>>(this.notaDebitoUrl + "/interesesV2", formData);
			case 1:
				return this.http.post<Response<any>>(this.notaDebitoUrl + "/anulacionV2", formData);
			default:
				return throwError(new Error("Tipo de nota no soportado"));
		}
	}

	emitirNotaCredito(usuarioId: string, sucursalId: string, nota: NotaCredito, dte: DteEmitido): Observable<Response<any>> {
		const formData = this.crearFormularioDte(usuarioId, sucursalId, dte);
		formData.append("motivo", nota.motivoString);
		if (nota.tipoNota === 1 || nota.tipoNota === 2) formData.append("detalleString", JSON.stringify(nota.itemsNota));
		const paths = ["/nota_credito/anulacionV2", "/nota_credito/corrige_montov2", "/nota_credito/corrige_textoV2"];
		const path = paths[nota.tipoNota];
		return path ? this.http.post<Response<any>>(this.dteUrl + path, formData) : throwError(new Error("Tipo de nota no soportado"));
	}

	obtenerFormatos(emisorId: string, tipoDteId: string): Observable<Response<FormatoDte[]>> {
		return this.http.get<Response<FormatoDte[]>>(this.facturacionUrl + "/getActiveTemplates/" + emisorId + "/" + tipoDteId);
	}

	obtenerCodigosAduana(): Observable<Response<CodigosAduana>> {
		return this.http.get<Response<CodigosAduana>>(this.exportacionUrl + "/codigos_aduana");
	}

	emitirExportacion(usuarioId: string, sucursalId: string, dte: DteEmitido): Observable<Response<any>> {
		return this.http.post<Response<any>>(this.exportacionUrl + "/emitirfactura", this.crearFormularioDte(usuarioId, sucursalId, dte));
	}

	obtenerPlantillas(emisorId: string, codigosTipoDte?: number[]): Observable<Response<PlantillaDte[]>> {
		let params = new HttpParams().set("emisorId", emisorId || "");
		(codigosTipoDte || []).forEach((codigo) => (params = params.append("codigosTipoDte", codigo.toString())));
		return this.http.get<Response<PlantillaDte[]>>(this.dteUrl + "/plantillas", { params });
	}

	actualizarPlantilla(dteId: string, esPlantilla: boolean, nombrePlantilla: string): Observable<Response<boolean>> {
		return this.http.post<Response<boolean>>(this.dteUrl + "/plantilla/guardar", { dteId, esPlantilla, nombrePlantilla });
	}

	obtenerGuiasFacturables(emisorId: string, receptorId: string, usuarioId: string): Observable<Response<DteEmitido[]>> {
		const formData = new FormData();
		formData.append("emisorId", emisorId);
		formData.append("receiverId", receptorId);
		formData.append("userId", usuarioId);
		return this.http.post<Response<DteEmitido[]>>(this.guiaUrl + "/getGuidesByClient", formData);
	}

	private crearFormularioDte(usuarioId: string, sucursalId: string, dte: IDte): FormData {
		const formData = new FormData();
		formData.append("usuarioIdString", usuarioId);
		formData.append("sucursalIdString", sucursalId);
		formData.append("dteEmitidoEntString", JSON.stringify(dte));
		return formData;
	}
}
